/**
 * Central de Projetos: ficha do cliente, no desenho da ficha de Clientes do
 * Legado (cabeçalho fixo, coluna de contexto, pedidos que abrem), com os
 * dados vindos da API da Central (sem o Worker da planilha).
 *
 * - Pedidos: o desta ficha aberto e os outros do mesmo cliente; dentro de
 *   cada um, os ambientes com a etapa atual da planilha e a leitura da IA
 *   (situação + estado), abrindo as pendências e decisões do ambiente.
 * - Pendências (com o histórico das fechadas), Conversas (com o atalho para
 *   abrir na tela de Conversas e os apelidos do cliente) e Decisões ficam em abas.
 * - Perfil (quem é quem, como o cliente está e o texto da IA): aba que só
 *   existe quando o servidor manda o `perfil` (quem lê as mensagens).
 */
import React, { useCallback, useMemo, useState } from "react";
import { FiArrowLeft, FiMessageCircle, FiRefreshCw } from "react-icons/fi";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import AsyncButton from "../../components/AsyncButton";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useAuth } from "../../context/AuthContext";
import { lerFicha, pedirAnalise } from "../../services/centralProjetos";
import { plural } from "../../utils/dinheiro";
import { textoAtraso } from "./apresentacao";
import { ErroDaCentral, PERM, useCarga } from "./comum";
import { BotaoCompacto } from "./conversas/estilos";
import { Apelidos, Conversas, Decisoes, Divergencias, Historico, Pendencias } from "./ficha/Acompanhamento";
import {
  Aba,
  Abas,
  CabecalhoFixo,
  Chip,
  ContextSticky,
  DetailRoot,
  AcoesDoCabecalho,
  BotaoVoltar,
  HeaderDot,
  HeaderIdentity,
  HeaderName,
  HeaderPill,
  HeaderPills,
  LinhaDoCabecalho,
  MainColumn,
  PainelDaAba,
  ThreeZoneGrid,
} from "./ficha/estilos";
import LateralDaFicha from "./ficha/Lateral";
import PedidosDaFicha from "./ficha/Pedidos";
import PerfilDoCliente from "./ficha/Perfil";
import { TarefasDoCliente } from "./NaAgenda";

const ABAS = Object.freeze([
  { id: "pedidos", rotulo: "Pedidos" },
  { id: "pendencias", rotulo: "Pendências" },
  { id: "conversas", rotulo: "Conversas" },
  { id: "decisoes", rotulo: "Decisões" },
  { id: "perfil", rotulo: "Perfil", soComPerfil: true },
]);

function rolarSuave() {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? "auto" : "smooth";
  } catch {
    return "auto";
  }
}

/** O pedido desta ficha primeiro; depois os outros do mesmo cliente (mesmo nome no Gerenciador). */
function pedidosDoCliente(dados) {
  if (!dados?.cliente) return [];
  const atual = { ...dados.cliente, ambientes: dados.ambientes || [], atual: true };
  const outros = (dados.outros_pedidos || [])
    .filter((p) => p && String(p.numero_pedido) !== String(atual.numero_pedido))
    .map((p) => ({ ...p, ambientes: p.ambientes || [], atual: false }));
  return [atual, ...outros];
}

/** A conversa mais recente do cliente, para o atalho do cabeçalho. */
function conversaMaisRecente(conversas) {
  return [...(conversas || [])].sort(
    (a, b) => new Date(b.ultima_mensagem_em || 0) - new Date(a.ultima_mensagem_em || 0),
  )[0] || null;
}

function Cabecalho({ cliente, pedido, pedidos, conversa, podeTratar, aoVoltar, aoAbrirConversa }) {
  const { request } = useAuth();
  const ambientes = pedidos.reduce((soma, p) => soma + p.ambientes.length, 0);
  return (
    <CabecalhoFixo>
      <LinhaDoCabecalho>
        <BotaoVoltar type="button" onClick={aoVoltar} aria-label="Voltar">
          <FiArrowLeft size={14} aria-hidden="true" />
          <span>Voltar</span>
        </BotaoVoltar>
        <HeaderIdentity>
          <HeaderName>
            <span className="name-text" title={cliente ? cliente.nome : `Pedido ${pedido}`}>
              {cliente ? cliente.nome : `Pedido ${pedido}`}
            </span>
          </HeaderName>
          {cliente ? (
            <HeaderPills>
              <HeaderPill>Pedido {cliente.numero_pedido}</HeaderPill>
              <HeaderDot aria-hidden="true">·</HeaderDot>
              <HeaderPill>{plural(pedidos.length, "pedido", "pedidos")}</HeaderPill>
              <HeaderDot aria-hidden="true">·</HeaderDot>
              <HeaderPill>{plural(ambientes, "ambiente", "ambientes")}</HeaderPill>
            </HeaderPills>
          ) : null}
        </HeaderIdentity>
        <AcoesDoCabecalho>
          {cliente ? (
            <Chip $tone={cliente.atraso_dias > 0 ? "danger" : "default"}>
              {cliente.atraso_dias > 0 ? textoAtraso(cliente.atraso_dias) : "No prazo"}
            </Chip>
          ) : null}
          {conversa ? (
            <BotaoCompacto
              type="button"
              aria-label="Abrir conversa"
              title={`Abrir ${conversa.nome} na tela de Conversas`}
              onClick={() => aoAbrirConversa(conversa)}
            >
              <FiMessageCircle size={15} aria-hidden="true" />
              <span className="rotulo-longo">Abrir conversa</span>
            </BotaoCompacto>
          ) : null}
          {podeTratar ? (
            <AsyncButton
              variant="secondary"
              aria-label="Atualizar análise"
              title="Pedir à IA que leia as conversas de novo"
              successMessage="Análise pedida: sai na próxima volta (até 1 minuto)."
              onClick={() => pedirAnalise(request, pedido)}
            >
              <FiRefreshCw aria-hidden="true" />
              <span className="rotulo-longo">Atualizar análise</span>
            </AsyncButton>
          ) : null}
        </AcoesDoCabecalho>
      </LinhaDoCabecalho>
    </CabecalhoFixo>
  );
}

function FichaDoPedido({ pedido }) {
  const navigate = useNavigate();
  const local = useLocation();
  const { hasPermission } = useAuth();
  const { dados, erro, carregando, recarregar } = useCarga((req) => lerFicha(req, pedido), [pedido]);
  const podeTratar = Boolean(hasPermission?.(PERM.TRATAR));
  const podeLerMensagens = Boolean(hasPermission?.(PERM.MENSAGENS));
  const [aba, setAba] = useState("pedidos");
  const [abertos, setAbertos] = useState(() => new Set([String(pedido)]));
  const [pedidoAtivo, setPedidoAtivo] = useState(String(pedido));

  const pedidos = useMemo(() => pedidosDoCliente(dados), [dados]);
  const ligacoes = useMemo(
    () => ({ pendencias: dados?.pendencias_abertas || [], decisoes: dados?.decisoes || [] }),
    [dados],
  );

  const alternarPedido = useCallback((numero) => {
    setAbertos((atuais) => {
      const novos = new Set(atuais);
      if (novos.has(numero)) novos.delete(numero);
      else novos.add(numero);
      return novos;
    });
  }, []);

  const abrirVarios = useCallback((numeros) => {
    setAbertos((atuais) => new Set([...atuais, ...numeros]));
  }, []);

  function irAoPedido(numero) {
    setAba("pedidos");
    setPedidoAtivo(numero);
    abrirVarios([numero]);
    window.requestAnimationFrame(() => {
      document.getElementById(`pedido-${numero}`)?.scrollIntoView({ behavior: rolarSuave(), block: "start" });
    });
  }

  function voltar() {
    // Veio de outra tela do app (Conversas, Painel): volta para ela.
    if (local.key && local.key !== "default") navigate(-1);
    else navigate("/central-projetos");
  }

  if (carregando && !dados) return <PageLoader scope="central-projetos" />;
  const cliente = dados?.cliente || null;
  const perfil = dados?.perfil || null;
  const abas = ABAS.filter((item) => !item.soComPerfil || perfil);
  const contagem = {
    pendencias: dados?.pendencias_abertas?.length || 0,
    conversas: dados?.conversas?.length || 0,
    decisoes: dados?.decisoes?.length || 0,
  };

  return (
    <DetailRoot>
      <Cabecalho
        cliente={cliente}
        pedido={pedido}
        pedidos={pedidos}
        conversa={conversaMaisRecente(dados?.conversas)}
        podeTratar={podeTratar}
        aoVoltar={voltar}
        aoAbrirConversa={(c) => navigate(`/central-projetos/conversas/${c.id}`)}
      />
      {erro ? (
        <div style={{ padding: 18 }}>
          <ErroDaCentral erro={erro} />
        </div>
      ) : null}
      {cliente ? (
        <ThreeZoneGrid>
          <ContextSticky aria-label="Contexto do cliente">
            <LateralDaFicha
              cliente={cliente}
              pedidos={pedidos}
              pendenciasAbertas={contagem.pendencias}
              pedidoAtivo={pedidoAtivo}
              aoIrAoPedido={irAoPedido}
            />
          </ContextSticky>
          <MainColumn>
            <Abas role="tablist" aria-label="Partes da ficha" $cinco={abas.length > 4}>
              {abas.map((item) => {
                const total = contagem[item.id];
                return (
                  <Aba
                    key={item.id}
                    type="button"
                    role="tab"
                    id={`aba-${item.id}`}
                    aria-selected={aba === item.id}
                    aria-controls={`painel-${item.id}`}
                    $ativa={aba === item.id}
                    onClick={() => setAba(item.id)}
                  >
                    <b>{item.rotulo}</b>
                    {total ? <span>{total}</span> : null}
                  </Aba>
                );
              })}
            </Abas>
            <PainelDaAba role="tabpanel" id={`painel-${aba}`} aria-labelledby={`aba-${aba}`}>
              {aba === "pedidos" ? (
                <PedidosDaFicha
                  pedidos={pedidos}
                  ligacoes={ligacoes}
                  abertos={abertos}
                  aoAlternar={alternarPedido}
                  aoAbrirVarios={abrirVarios}
                  aoAbrirFicha={(numero) => navigate(`/central-projetos/clientes/${encodeURIComponent(numero)}`)}
                />
              ) : null}
              {aba === "pendencias" ? (
                <>
                  <Divergencias divergencias={cliente.divergencias || []} />
                  <Pendencias abertas={dados.pendencias_abertas || []} podeTratar={podeTratar} aoMudar={recarregar} />
                  <TarefasDoCliente pedido={pedido} />
                  <Historico fechadas={dados.pendencias_fechadas || []} />
                </>
              ) : null}
              {aba === "conversas" ? (
                <>
                  <Conversas
                    pedido={pedido}
                    conversas={dados.conversas || []}
                    podeLer={dados.pode_ler_mensagens}
                    podeTratar={podeTratar}
                    aoMudar={recarregar}
                  />
                  <Apelidos pedido={pedido} apelidos={dados.apelidos || []} podeTratar={podeTratar} aoMudar={recarregar} />
                </>
              ) : null}
              {aba === "decisoes" ? <Decisoes decisoes={dados.decisoes || []} /> : null}
              {aba === "perfil" && perfil ? (
                <PerfilDoCliente
                  pedido={pedido}
                  perfil={perfil}
                  podeTratar={podeTratar}
                  podeEscrever={podeLerMensagens}
                  aoMudar={recarregar}
                />
              ) : null}
            </PainelDaAba>
          </MainColumn>
        </ThreeZoneGrid>
      ) : null}
    </DetailRoot>
  );
}

/** Trocar de pedido (atalho de outro pedido do cliente) começa a ficha do zero. */
export default function FichaCliente() {
  const { pedido } = useParams();
  return <FichaDoPedido key={pedido} pedido={pedido} />;
}
